import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Save, ListChecks, History } from "lucide-react";
import { toast } from "sonner";

const emptyExam = () => ({
  title: "",
  exam_type: "LGS",
  year: new Date().getFullYear(),
  subject: "",
  source_link: "",
  question_ids: [],
});

export default function AdminPastExams() {
  const [exams, setExams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExam());
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get(`${V1}/past-exams`).then((r) => setExams(r.data || []));

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing(null);
    setForm(emptyExam());
    setOpen(true);
  };

  const startEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title || "",
      exam_type: e.exam_type || "LGS",
      year: e.year || new Date().getFullYear(),
      subject: e.subject || "",
      source_link: e.source_link || "",
      question_ids: e.question_ids || [],
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        year: parseInt(form.year) || new Date().getFullYear(),
      };
      if (editing) {
        await api.patch(`${V1}/past-exams/${editing.id}`, payload);
        toast.success("Sınav güncellendi");
      } else {
        await api.post(`${V1}/past-exams`, payload);
        toast.success("Sınav oluşturuldu");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu sınavı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`${V1}/past-exams/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-6" data-testid="admin-past-exams-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Sınav Yönetimi</div>
          <h1 className="font-display text-3xl font-bold">Çıkmış Sorular (ÖSYM)</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            ÖSYM geçmiş yıl sınavlarını veya hazır denemeleri sisteme ekleyin.
          </p>
        </div>
        <Button onClick={startNew} data-testid="new-past-exam-btn">
          <Plus className="h-4 w-4" /> Yeni Ekle
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Başlık</th>
              <th className="text-left p-3">Tür</th>
              <th className="text-left p-3">Yıl</th>
              <th className="text-left p-3">Soru Sayısı</th>
              <th className="text-right p-3">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {exams.length === 0 && (
              <tr>
                <td colSpan={5} className="p-8 text-center text-muted-foreground">
                  Henüz geçmiş sınav yok.
                </td>
              </tr>
            )}
            {exams.map((e) => (
              <tr key={e.id} className="border-t border-border" data-testid={`admin-past-row-${e.id}`}>
                <td className="p-3">
                  <div className="font-medium">{e.title}</div>
                </td>
                <td className="p-3">{e.exam_type}</td>
                <td className="p-3">{e.year}</td>
                <td className="p-3">{e.question_count ?? (e.question_ids?.length || 0)}</td>
                <td className="p-3 text-right">
                  {/* We don't have past_exam_id filter yet in AdminQuestions, but the user can use standard question UI */}
                  <Button size="sm" variant="ghost" onClick={() => startEdit(e)} data-testid={`edit-past-${e.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`delete-past-${e.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="past-exam-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Sınavı Düzenle" : "Yeni Çıkmış Sınav"}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Başlık *</Label>
              <Input value={form.title} onChange={(e) => setF("title", e.target.value)} />
            </div>
            <div>
              <Label>Sınav Türü</Label>
              <Select value={form.exam_type} onValueChange={(v) => setF("exam_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LGS">LGS</SelectItem>
                  <SelectItem value="TYT">TYT</SelectItem>
                  <SelectItem value="AYT">AYT</SelectItem>
                  <SelectItem value="DENEME">DENEME</SelectItem>
                  <SelectItem value="GENEL">GENEL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Yıl</Label>
              <Input type="number" value={form.year} onChange={(e) => setF("year", e.target.value)} />
            </div>
            <div>
              <Label>Ders (opsiyonel)</Label>
              <Input value={form.subject} onChange={(e) => setF("subject", e.target.value)} />
            </div>
            <div>
              <Label>Kaynak URL (opsiyonel)</Label>
              <Input value={form.source_link} onChange={(e) => setF("source_link", e.target.value)} placeholder="https://..." />
            </div>
            <div className="md:col-span-2">
              <Label>Soru ID'leri (virgülle ayır)</Label>
              <Input 
                value={(form.question_ids || []).join(",")} 
                onChange={(e) => setF("question_ids", e.target.value.split(",").map(v => v.trim()).filter(Boolean))}
                placeholder="qid1, qid2, qid3..." 
              />
              <p className="text-xs text-muted-foreground mt-1">
                Soru havuzundaki soruların ID'lerini virgülle ayırarak girebilirsiniz.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} disabled={busy || !form.title} data-testid="past-form-save">
              <Save className="h-4 w-4" /> {busy ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
